import { useState } from 'react';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import {
  useVendors,
  useDeleteVendor,
  vendorDisplayName,
} from '../api/queries/vendors';
import { VendorFormModal } from '../components/VendorFormModal';
import { IconAction } from '../components/IconAction';
import { SortableTh } from '../components/SortableTh';
import { useClientSort } from '../hooks/useClientSort';
import { formatPhoneDisplay } from '../utils/phone';
import type { Vendor } from '@stfors/shared-types';
import './VendorsPage.css';
import { TableScroll } from '../components/TableScroll';

type VendorSortKey = 'name' | 'location' | 'phone' | 'tax_category';

// Vendors list page. Previously a tab inside Settings; now a first-class
// member of the Directory section's sub-tab bar (Directory | Clients |
// Contractors | Vendors). Lists every active vendor (soft-deleted excluded).
// Add/Edit open the shared <VendorFormModal>. Soft-delete goes through the
// standard confirm() prompt — historical expenses keep their name snapshot,
// so soft-deleting a vendor only affects future expense entries.
//
// Like the other directory pages, this self-gates: the API enforces
// vendor.read (a 403 renders a permission message) and write actions are
// hidden unless can('vendor.write').
export function VendorsPage(): JSX.Element {
  const vendorsQ = useVendors();
  const del = useDeleteVendor();
  const canWrite = can('vendor.write');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Vendor | null>(null);
  const [error, setError] = useState<string | null>(null);

  const openAdd = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (v: Vendor) => { setEditing(v); setModalOpen(true); };

  const onDelete = async (v: Vendor) => {
    setError(null);
    if (!confirm(`Remove "${vendorDisplayName(v)}" from the vendor list?`)) return;
    try {
      await del.mutateAsync(v.id);
    } catch {
      setError(`Could not remove "${v.name}".`);
    }
  };

  const vendors = vendorsQ.data?.vendors ?? [];
  const sort = useClientSort(vendors, (v, key: VendorSortKey) => {
    switch (key) {
      case 'name': return v.name;
      case 'location': return [v.city, v.state].filter(Boolean).join(', ');
      case 'phone': return v.phone ?? '';
      case 'tax_category': return v.default_tax_category ?? '';
      default: return '';
    }
  }, { key: 'name', dir: 'asc' });

  return (
    <section className="section-shell">
      <header className="section-header">
        <h2>Vendors</h2>
        <div className="vendors-header-actions">
          <span className="vendors-count">{vendors.length} total</span>
          {canWrite && (
            <button type="button" className="btn-cyan-primary" onClick={openAdd}>
              + Add vendor
            </button>
          )}
        </div>
      </header>

      <p className="vendors-description">
        Reusable business records for expense tracking. Editing a vendor&apos;s name won&apos;t change historical expense entries — those keep the name that was on file at the time the expense was recorded.
      </p>

      {vendorsQ.isPending ? (
        <p className="vendors-status">Loading vendors…</p>
      ) : vendorsQ.error ? (
        <p className="vendors-status vendors-error">
          {vendorsQ.error instanceof ApiError && vendorsQ.error.status === 403
            ? "You don't have permission to view vendors."
            : 'Failed to load vendors. Try again in a moment.'}
        </p>
      ) : vendors.length === 0 ? (
        <div className="section-body">
          <p>No vendors yet{canWrite ? '. Add your first one above.' : '.'}</p>
        </div>
      ) : (
        <div className="section-body section-body-flush">
          <TableScroll id="vendors">
            <table className="vendors-table">
              <thead>
                <tr>
                  <SortableTh column="name" label="Name" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                  <SortableTh column="location" label="Location" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                  <SortableTh column="phone" label="Phone" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                  <SortableTh column="tax_category" label="Default tax category" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                  <th />
                </tr>
              </thead>
              <tbody>
                {sort.sorted.map((v) => (
                  <tr key={v.id}>
                    <td>
                      <strong>{v.name}</strong>
                      {v.dba_name && v.legal_name && (
                        <div className="vendors-dba">Legal: {v.legal_name}</div>
                      )}
                    </td>
                    <td>{[v.city, v.state].filter(Boolean).join(', ') || '—'}</td>
                    <td>{formatPhoneDisplay(v.phone)}</td>
                    <td>{v.default_tax_category ?? '—'}</td>
                    <td className="vendors-actions">
                      {canWrite && (
                        <>
                          <IconAction
                            action="edit"
                            onClick={() => openEdit(v)}
                            iconSize={16}
                          />
                          <IconAction
                            action="delete"
                            label="Remove"
                            onClick={() => onDelete(v)}
                            disabled={del.isPending}
                            iconSize={16}
                          />
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
              </TableScroll>
        </div>
      )}

      {error && <p className="vendors-error" role="alert">{error}</p>}

      <VendorFormModal
        open={modalOpen}
        initial={editing}
        onClose={() => setModalOpen(false)}
      />
    </section>
  );
}
